import "../styles/CheckList.css";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ChevronRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

function CheckList() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [openSections, setOpenSections] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tenderTitle, setTenderTitle] = useState("");

  const tenderId = id;
  
  // Group documents by category
  const groupedDocs = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {});

  // Fetch master documents and existing tender documents
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch master documents
        const docsRes = await fetch(`${API_BASE_URL}/api/master-documents`);
        if (!docsRes.ok) throw new Error("Failed to fetch master documents");
        const docsData = await docsRes.json();
        setDocuments(docsData);

        // Fetch tender details to get existing documents
        const tenderRes = await axios.get(`${API_BASE_URL}/api/tenders/${id}`, {
          params: { userId: localStorage.getItem('userId') }
        });
        
        if (tenderRes.data?.tender) {
          const tenderData = tenderRes.data.tender;
          setTenderTitle(tenderData.title || tenderData.work_name || 'Tender');
          
          // Get existing additional documents
          const existingDocs = tenderData.additional_documents || 
                              tenderData.required_documents_list || 
                              tenderData.required_documents_from_table || 
                              [];
          
          // Map existing document names to master document IDs
          const existingIds = [];
          const existingNames = existingDocs.map(doc => 
            typeof doc === 'string' ? doc.toLowerCase() : (doc.name || '').toLowerCase()
          );
          
          // Find matching master documents
          docsData.forEach(masterDoc => {
            if (existingNames.some(name => 
              masterDoc.name.toLowerCase().includes(name) || 
              name.includes(masterDoc.name.toLowerCase())
            )) {
              existingIds.push(masterDoc.id);
            }
          });
          
          setSelectedDocs(existingIds);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load documents");
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchData();
    }
  }, [id]);

  const toggleSection = (category) => {
    setOpenSections(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSelect = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const handleSelectAll = (docs) => {
    const ids = docs.map(d => d.id);
    const allSelected = ids.every(id => selectedDocs.includes(id));

    if (allSelected) {
      setSelectedDocs(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedDocs(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const handleSave = async () => {
    if (selectedDocs.length === 0) {
      toast.warning("Please select at least one document");
      return;
    }

    setSaving(true);
    try {
      // Get selected document details
      const selectedDocDetails = [];
      documents.forEach(doc => {
        if (selectedDocs.includes(doc.id)) {
          selectedDocDetails.push({
            id: doc.id,
            name: doc.name,
            type: doc.type || "Required",
            category: doc.category || "General"
          });
        }
      });

      // Save to backend
      const response = await fetch(`${API_BASE_URL}/api/tender/${tenderId}/documents`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          documentIds: selectedDocs,
          documents: selectedDocDetails,
          tenderId: tenderId
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save documents");
      }

      const result = await response.json();
      
      toast.success(`✓ ${selectedDocs.length} document(s) selected successfully`);

      // Navigate back to tender details with state
      navigate(`/tenders/${tenderId}`, { 
        state: { 
          sourceTab: 'active',
          documentsUpdated: true,
          selectedDocuments: selectedDocDetails
        }
      });

    } catch (error) {
      console.error("Error saving documents:", error);
      toast.error(error.message || "Failed to save documents");
    } finally {
      setSaving(false);
    }
  };

  // Get total documents count
  const getTotalCount = () => {
    let count = 0;
    Object.keys(groupedDocs).forEach(cat => {
      count += groupedDocs[cat].length;
    });
    return count;
  };

  const categoryStyles = {
    "Company Details": {
      color: "#2F7A87",
      bg: "#E8F3F1",
      icon: "/images/Company Documents.svg",
    },
    "ISO": {
      color: "#2E7D32",
      bg: "#E8F5E9",
      icon: "/images/ISO Details.svg",
    },
    "Finance": {
      color: "#C62828",
      bg: "#FDECEA",
      icon: "/images/Financesv.svg",
    },
    "Declarations": {
      color: "#6A1B9A",
      bg: "#F3E5F5",
      icon: "/images/Declarations.svg",
    },
    "Statutory": {
      color: "#EF6C00",
      bg: "#FFF3E0",
      icon: "/images/Statutory.svg",
    },
    "Legal": {
      color: "#1565C0",
      bg: "#E3F2FD",
      icon: "/images/Legalsv.svg",
    },
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <nav aria-label="breadcrumb" style={{ "--bs-breadcrumb-divider": "''" }}>
        <ol className="breadcrumb d-flex align-items-center py-2 mb-0">
          <li className="breadcrumb-item d-flex align-items-center">
            <Link to="/" className="text-decoration-none text-secondary">
              Home
            </Link>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>

          <li className="breadcrumb-item d-flex align-items-center">
            <Link to="/TenderList" className="text-secondary">
              TenderList
            </Link>
            <ChevronRight size={16} className="mx-1 text-secondary" />
          </li>

          <li className="breadcrumb-item fw-semibold text-primary" style={{color:"#2F7A87"}}>
            {tenderTitle || "Document Checklist"}
          </li>
        </ol>
      </nav>

      <div className="p-4">

        {/* HEADER */}
        <div className="d-flex align-items-center gap-2">
          <ArrowLeft size={24} onClick={() => navigate(-1)} style={{ cursor: "pointer" }} />
          <h4 className="mb-0">Document Checklist</h4>
        </div>

        {/* TOP RIGHT */}
        <div className="d-flex justify-content-between align-items-center gap-3 mt-3 ms-5">
          <span>
            <strong>{selectedDocs.length}</strong> of <strong>{getTotalCount()}</strong> documents selected
          </span>

          <button
            className="btn open-checklist-button btn-sm d-flex align-items-center gap-1"
            onClick={handleSave}
            disabled={saving || selectedDocs.length === 0}
            style={{
              backgroundColor: selectedDocs.length > 0 ? "#2F7A87" : "#9ca3af",
              border: "none",
              color: "white",
              padding: "8px 20px",
              borderRadius: "8px"
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                Confirm Selection ({selectedDocs.length})
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>

        {/* INFO BOX */}
        <div className="content-explain mt-3 p-3 border rounded col-11 ms-5">
          Check the box next to every document you have available. Only checked
          documents will appear in the upload section on your tender page.
          Use <strong>Select all</strong> within a category for quick selection.
        </div>

        {Object.keys(groupedDocs).length === 0 ? (
          <div className="text-center mt-5">
            <p>No documents available</p>
          </div>
        ) : (
          Object.keys(groupedDocs).map((category) => {
            const docs = groupedDocs[category];
            const isOpen = openSections[category] ?? true;
            const style = categoryStyles[category] || {};
            const allSelected = docs.every(d => selectedDocs.includes(d.id));

            return (
              <div key={category} className="doc-card mt-3 col-11 ms-5">

                {/* HEADER */}
                <div
                  className="doc-header d-flex justify-content-between align-items-center"
                  onClick={() => toggleSection(category)}
                  style={{ backgroundColor: style.bg || "#f8f9fa" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    
                    {/* ICON */}
                    <img
                      src={style.icon || "/images/default-icon.svg"}
                      alt="icon"
                      width="35"
                      height="35"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-icon.svg";
                      }}
                    />

                    <div>
                      <div
                        className="fw-semibold"
                        style={{ color: style.color || "#2F7A87" }}
                      >
                        {category}
                      </div>

                      <small>{docs.filter(d => selectedDocs.includes(d.id)).length} of {docs.length} selected</small>
                    </div>
                  </div>

                  {isOpen ? <ChevronUp /> : <ChevronDown />}
                </div>

                {/* BODY */}
                {isOpen && (
                  <>
                    <div className="p-2 border-bottom">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => handleSelectAll(docs)}
                        style={{ marginRight: "8px" }}
                      />
                      <label style={{ cursor: "pointer" }}>
                        {allSelected ? "Deselect all" : "Select all"}
                      </label>
                    </div>

                    {docs.map((doc, index) => (
                      <div
                        key={doc.id}
                        className="doc-rows d-flex justify-content-between px-3 py-3 border-bottom"
                        style={{
                          backgroundColor: selectedDocs.includes(doc.id) ? "#f0f9ff" : "transparent"
                        }}
                      >
                        <div className="d-flex gap-3">
                          <div className="doc-number">{index + 1}</div>

                          <div>
                            <span style={{ fontWeight: selectedDocs.includes(doc.id) ? "600" : "400" }}>
                              {doc.name}
                            </span>
                            <div className="doc-tag">{doc.type || "Document"}</div>
                          </div>
                        </div>

                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.id)}
                          onChange={() => handleSelect(doc.id)}
                          style={{ accentColor: "#2F7A87" }}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })
        )}

        {/* BOTTOM ACTION BAR */}
        <div className="d-flex justify-content-between align-items-center mt-4 col-11 ms-5">
          <div>
            {selectedDocs.length > 0 ? (
              <span style={{ color: "#059669", fontWeight: "600" }}>
                ✓ {selectedDocs.length} document(s) selected
              </span>
            ) : (
              <span style={{ color: "#6b7280" }}>No documents selected</span>
            )}
          </div>
          <button
            className="btn open-checklist-button d-flex align-items-center gap-2"
            onClick={handleSave}
            disabled={saving || selectedDocs.length === 0}
            style={{
              backgroundColor: selectedDocs.length > 0 ? "#2F7A87" : "#9ca3af",
              border: "none",
              color: "white",
              padding: "10px 28px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600"
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                Confirm Selection ({selectedDocs.length})
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>

      </div>
    </>
  );
}

export default CheckList;